<script setup lang="ts">
  import { ref, computed, onMounted } from 'vue'
  import { storeToRefs } from 'pinia'
  import SettingsPage from '@/components/app/settings/SettingsPage.vue'
  import {
    type TUser,
    type TAiSettings,
    AiSettingsService,
    type NotificationSettings
  } from '@/types/generated'
  import Switch from '@/components/ui/switch/Switch.vue'
  import { useUserStore } from '@/shared/stores/userStore'
  import { genericErrorToast } from '@/lib/toasts'
  import { useTranslation } from '@/composables/useTranslation'
  import { MagnifyingGlassIcon, BellAlertIcon } from '@heroicons/vue/24/outline'

  const userStore = useUserStore()
  const { fetchUser } = userStore
  const { user } = storeToRefs(userStore)

  type NotificationAction = {
    key: string
    name: { de?: string; en?: string }
    description: { de?: string; en?: string }
    help_text: { de?: string; en?: string }
    notify_on_auto_commit_default?: boolean
    notify_on_pending_commit_default?: boolean
  }

  type NotificationSetting = {
    notification_settings_key: string
    notify_on_auto_commit: boolean
    notify_on_pending_commit: boolean
  }

  const { t } = useTranslation({
    de: {
      title: 'KI‑Benachrichtigungen',
      intro: 'Legen Sie fest, worüber Sie bei KI‑Aktionen informiert werden möchten.',
      bundledTitle: 'Benachrichtigungseinstellungen',
      bundledIntro: 'Konfigurieren Sie, wie Sie Benachrichtigungen erhalten möchten.',
      bundledLabel: 'Benachrichtigungen bündeln',
      bundledDesc:
        'Mehrere Benachrichtigungen zu einer E-Mail zusammenfassen, anstatt sie einzeln zu senden.',
      bundledDelayLabel: 'Maximale Verzögerung',
      bundledDelayDesc:
        'Wie lange maximal gewartet wird, bevor gebündelte Benachrichtigungen gesendet werden (in Minuten).',
      groupTitle: 'Aktionsbenachrichtigungen',
      groupIntro: 'Bestimmen Sie für jede KI‑Aktion, wann Sie benachrichtigt werden.',
      noActions: 'Keine Aktionen verfügbar.',
      loading: 'Einstellungen werden geladen …',
      autoLabel: 'Bei automatischer Ausführung',
      autoDesc: 'Benachrichtigen, wenn die KI diese Aktion automatisch ausführt.',
      pendingLabel: 'Wenn Bestätigung aussteht',
      pendingDesc: 'Benachrichtigen, wenn diese Aktion auf Ihre Bestätigung wartet.',
      searchPlaceholder: 'Aktionen durchsuchen …',
      colAction: 'Aktion',
      colAuto: 'KI-ausgeführt',
      colPending: 'KI-Vorschlag',
      bulkOn: 'Alle an',
      bulkOff: 'Alle aus'
    },
    en: {
      title: 'AI notifications',
      intro: 'Choose what you want to be notified about for AI actions.',
      bundledTitle: 'Notification settings',
      bundledIntro: 'Configure how you want to receive notifications.',
      bundledLabel: 'Bundle notifications',
      bundledDesc:
        'Group multiple notifications into a single email instead of sending them individually.',
      bundledDelayLabel: 'Maximum delay',
      bundledDelayDesc:
        'How long to wait at most before sending bundled notifications (in minutes).',
      groupTitle: 'Action notifications',
      groupIntro: 'Decide for each AI action when you want to be notified.',
      noActions: 'No actions available.',
      loading: 'Loading settings …',
      autoLabel: 'On automatic execution',
      autoDesc: 'Notify me when the AI runs this action automatically.',
      pendingLabel: 'When approval is pending',
      pendingDesc: 'Notify me when this action is waiting for my approval.',
      searchPlaceholder: 'Search actions …',
      colAction: 'Action',
      colAuto: 'Automatic',
      colPending: 'Approval',
      bulkOn: 'All on',
      bulkOff: 'All off'
    },
    fr: {
      title: 'Notifications de l’IA',
      intro: 'Choisissez les actions de l’IA pour lesquelles vous souhaitez être informé.',
      bundledTitle: 'Paramètres de notification',
      bundledIntro: 'Configurez la manière dont vous souhaitez recevoir les notifications.',
      bundledLabel: 'Regrouper les notifications',
      bundledDesc:
        'Regrouper plusieurs notifications dans un seul e-mail au lieu de les envoyer individuellement.',
      bundledDelayLabel: 'Délai maximum',
      bundledDelayDesc:
        'Délai maximum d’attente avant l’envoi des notifications groupées (en minutes).',
      groupTitle: 'Notifications par action',
      groupIntro: 'Décidez pour chaque action de l’IA quand vous souhaitez être informé.',
      noActions: 'Aucune action disponible.',
      loading: 'Chargement des paramètres…',
      autoLabel: 'Lors d’une exécution automatique',
      autoDesc: 'Me prévenir lorsque l’IA exécute automatiquement cette action.',
      pendingLabel: 'En attente d’approbation',
      pendingDesc: 'Me prévenir lorsque cette action attend mon approbation.',
      searchPlaceholder: 'Rechercher des actions…',
      colAction: 'Action',
      colAuto: 'Automatique',
      colPending: 'Approbation',
      bulkOn: 'Tout activer',
      bulkOff: 'Tout désactiver'
    }
  })

  const isLoading = ref(false)
  const isSaving = ref(false)
  const aiSettings = ref<TAiSettings | null>(null)
  const availableActions = ref<NotificationAction[]>([])
  const searchQuery = ref('')

  // Bundled notifications settings
  const bundledNotifications = ref(true)
  const bundledDelayMinutes = ref(5)

  const getCurrentNotificationSettings = (): NotificationSetting[] => {
    if (!aiSettings.value) return []
    const settings = (aiSettings.value as any).notification_settings
    return Array.isArray(settings) ? (settings as NotificationSetting[]) : []
  }

  const filteredActions = computed(() => {
    const q = searchQuery.value.trim().toLowerCase()
    if (!q) return availableActions.value
    return availableActions.value.filter((a) => {
      const name = (a.name.de || a.name.en || '').toLowerCase()
      const desc = (a.description.de || a.description.en || '').toLowerCase()
      return name.includes(q) || desc.includes(q) || a.key.toLowerCase().includes(q)
    })
  })

  // Convert bundled delay to seconds for backend
  const bundledDelayInSeconds = computed(() => bundledDelayMinutes.value * 60)

  onMounted(async () => {
    const fetching: Array<Promise<unknown>> = []
    if (!user.value) {
      fetching.push(fetchUser())
    }
    await Promise.all(fetching)
    await loadSettings()
  })

  const loadSettings = async () => {
    try {
      isLoading.value = true

      const settingsResponse = await AiSettingsService.aiSettingsRetrieve({ uuid: 'current' })
      aiSettings.value = settingsResponse

      // Initialize bundled notification settings
      const settings = aiSettings.value as any
      bundledNotifications.value = settings.send_notifications_bundled ?? true

      // Convert seconds to minutes (round to nearest minute)
      const delaySeconds = settings.bundled_notifications_max_delay ?? 300
      bundledDelayMinutes.value = Math.round(delaySeconds / 60)

      const actionsResponse =
        await AiSettingsService.aiSettingsAvailableNotificationActionsRetrieve()
      const params = (actionsResponse as NotificationSettings).params || []
      availableActions.value = params as unknown as NotificationAction[]
    } catch (error) {
      console.error('Error loading notification settings:', error)
      genericErrorToast()
    } finally {
      isLoading.value = false
    }
  }

  const updateNotificationSetting = async (
    actionKey: string,
    field: 'notify_on_auto_commit' | 'notify_on_pending_commit',
    value: boolean
  ) => {
    if (!aiSettings.value || isSaving.value) return

    try {
      isSaving.value = true

      const existingSettings = getCurrentNotificationSettings()
      const existingSetting = existingSettings.find(
        (s) => s.notification_settings_key === actionKey
      )

      let updatedSettings: NotificationSetting[]
      if (existingSetting) {
        updatedSettings = existingSettings.map((setting) =>
          setting.notification_settings_key === actionKey ? { ...setting, [field]: value } : setting
        )
      } else {
        const defaultAction = availableActions.value.find((a) => a.key === actionKey)
        updatedSettings = [
          ...existingSettings,
          {
            notification_settings_key: actionKey,
            notify_on_auto_commit:
              field === 'notify_on_auto_commit'
                ? value
                : (defaultAction?.notify_on_auto_commit_default ?? false),
            notify_on_pending_commit:
              field === 'notify_on_pending_commit'
                ? value
                : (defaultAction?.notify_on_pending_commit_default ?? true)
          }
        ]
      }

      const response = await AiSettingsService.aiSettingsPartialUpdate({
        uuid: (aiSettings.value as any).uuid,
        requestBody: {
          notification_settings: updatedSettings as any
        }
      })

      aiSettings.value = response
    } catch (error) {
      console.error('Error updating notification settings:', error)
      genericErrorToast()
    } finally {
      isSaving.value = false
    }
  }

  const getNotificationSetting = (
    actionKey: string,
    field: 'notify_on_auto_commit' | 'notify_on_pending_commit'
  ): boolean => {
    const settings = getCurrentNotificationSettings()
    const setting = settings.find((s) => s.notification_settings_key === actionKey)
    return setting
      ? (setting as Record<typeof field, boolean>)[field]
      : getDefaultValue(actionKey, field)
  }

  const getDefaultValue = (
    actionKey: string,
    field: 'notify_on_auto_commit' | 'notify_on_pending_commit'
  ): boolean => {
    const action = availableActions.value.find((a) => a.key === actionKey)
    if (!action) return field === 'notify_on_pending_commit'
    return field === 'notify_on_auto_commit'
      ? (action.notify_on_auto_commit_default ?? false)
      : (action.notify_on_pending_commit_default ?? true)
  }

  const updateBundledNotificationsSettings = async () => {
    if (!aiSettings.value || isSaving.value) return

    try {
      isSaving.value = true

      const response = await AiSettingsService.aiSettingsPartialUpdate({
        uuid: (aiSettings.value as any).uuid,
        requestBody: {
          send_notifications_bundled: bundledNotifications.value,
          bundled_notifications_max_delay: bundledDelayInSeconds.value
        } as any
      })

      aiSettings.value = response
    } catch (error) {
      console.error('Error updating bundled notification settings:', error)
      genericErrorToast()
    } finally {
      isSaving.value = false
    }
  }

  const handleBundledNotificationsToggle = (value: boolean) => {
    bundledNotifications.value = value
    updateBundledNotificationsSettings()
  }
</script>

<template>
  <SettingsPage v-if="user">
    <template #actions></template>

    <div
      v-if="isLoading"
      class="flex items-center justify-center py-8"
    >
      <div class="text-gray-500">{{ t('loading') }}</div>
    </div>

    <div
      v-else
      class="space-y-6"
    >
      <div class="space-y-2">
        <h2 class="text-lg font-semibold text-gray-900">{{ t('title') }}</h2>
        <p class="text-sm text-gray-600">
          {{ t('intro') }}
        </p>
      </div>

      <!-- Bundled Notifications Settings -->
      <div class="w-full max-w-5xl space-y-4">
        <h3 class="text-md flex items-center gap-2 font-medium text-gray-900">
          <BellAlertIcon class="size-5 text-purple-600" />
          <span>{{ t('bundledTitle') }}</span>
        </h3>
        <p class="text-sm text-gray-600">{{ t('bundledIntro') }}</p>

        <div class="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
          <div class="space-y-4 px-4 py-4">
            <!-- Bundle notifications toggle -->
            <div class="flex items-center justify-between">
              <div class="flex-1 pr-4">
                <h4 class="text-sm font-medium text-gray-900">{{ t('bundledLabel') }}</h4>
                <p class="text-xs text-gray-500">{{ t('bundledDesc') }}</p>
              </div>
              <Switch
                :checked="bundledNotifications"
                :disabled="isSaving"
                class="data-[state=checked]:bg-purple-600 data-[state=unchecked]:bg-gray-200"
                @update:checked="handleBundledNotificationsToggle"
              />
            </div>

            <!-- Delay setting - only shown when bundling is enabled -->
            <div
              v-if="bundledNotifications"
              class="border-t border-gray-100 pt-4"
            >
              <div class="space-y-2">
                <h4 class="text-sm font-medium text-gray-900">{{ t('bundledDelayLabel') }}</h4>
                <p class="text-xs text-gray-500">{{ t('bundledDelayDesc') }}</p>
                <div class="flex items-center space-x-2">
                  <input
                    v-model.number="bundledDelayMinutes"
                    type="number"
                    min="1"
                    max="60"
                    class="w-20 rounded-md border border-gray-200 px-3 py-1.5 text-sm shadow-sm focus:border-purple-500
                      focus:outline-none focus:ring-2 focus:ring-purple-100"
                    @change="updateBundledNotificationsSettings"
                  />
                  <span class="text-sm text-gray-600">minutes</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="w-full max-w-5xl space-y-4">
        <h3 class="text-md flex items-center gap-2 font-medium text-gray-900">
          <BellAlertIcon class="size-5 text-purple-600" />
          <span>{{ t('groupTitle') }}</span>
        </h3>
        <p class="text-sm text-gray-600">{{ t('groupIntro') }}</p>

        <div class="relative">
          <input
            v-model="searchQuery"
            :placeholder="t('searchPlaceholder')"
            class="w-full rounded-lg border border-gray-200 bg-white py-2.5 pe-3 ps-10 text-sm shadow-sm
              focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-100"
            type="text"
          />
          <MagnifyingGlassIcon
            class="pointer-events-none absolute left-3 top-1/2 size-5 -translate-y-1/2 text-gray-400"
          />
        </div>

        <div
          v-if="filteredActions.length === 0"
          class="text-sm text-gray-500"
        >
          {{ t('noActions') }}
        </div>

        <div
          v-else
          class="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm"
        >
          <div
            class="hidden grid-cols-12 items-center bg-gray-50 px-4 py-3 text-xs font-medium text-gray-600 md:grid"
          >
            <div class="col-span-7">{{ t('colAction') }}</div>
            <div class="col-span-2 flex items-center justify-end">
              <span>{{ t('colAuto') }}</span>
            </div>
            <div class="col-span-3 flex items-center justify-end">
              <span>{{ t('colPending') }}</span>
            </div>
          </div>

          <div class="divide-y divide-gray-200">
            <div
              v-for="action in filteredActions"
              :key="action.key"
              class="grid grid-cols-1 gap-4 px-4 py-4 md:grid-cols-12 md:gap-2"
            >
              <div class="md:col-span-7">
                <h4 class="text-sm font-medium text-gray-900">
                  {{ action.name.de || action.name.en }}
                </h4>
                <p class="text-xs text-gray-500">
                  {{ action.description.de || action.description.en }}
                </p>
                <p class="text-xs text-gray-400">
                  {{ action.help_text.de || action.help_text.en }}
                </p>
              </div>

              <div class="flex items-center justify-between md:col-span-2 md:justify-end">
                <Switch
                  :checked="getNotificationSetting(action.key, 'notify_on_auto_commit')"
                  :disabled="isSaving"
                  class="data-[state=checked]:bg-purple-600 data-[state=unchecked]:bg-gray-200"
                  @update:checked="
                    updateNotificationSetting(action.key, 'notify_on_auto_commit', $event)
                  "
                />
              </div>

              <div class="flex items-center justify-between md:col-span-3 md:justify-end">
                <Switch
                  :checked="getNotificationSetting(action.key, 'notify_on_pending_commit')"
                  :disabled="isSaving"
                  class="data-[state=checked]:bg-purple-600 data-[state=unchecked]:bg-gray-200"
                  @update:checked="
                    updateNotificationSetting(action.key, 'notify_on_pending_commit', $event)
                  "
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </SettingsPage>
</template>
